import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Switch,
    Snackbar,
    Alert,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Navigate } from 'react-router-dom';
import { loadVariableSettings, saveVariableSettings } from './services/variableService';

const VariableManager = ({ loggedIn }) => {
    const [variables, setVariables] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [expandedCategory, setExpandedCategory] = useState('basic');
    const [newVariable, setNewVariable] = useState({ id: '', label: '', category: '', showInPopup: false, showInTrends: false });
    const [editMode, setEditMode] = useState(false);
    const [editingVariable, setEditingVariable] = useState(null);
    const [variableToDelete, setVariableToDelete] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // Categories configuration
    const categories = {
        basic: {
            title: "Basic Information",
            variables: ["year", "state_county"]
        },
        income: {
            title: "Income & Financial",
            variables: ["avg_ann_income_indv", "avg_mortgage_payment", "avg_ann_income_family", "indv_no_foodstamps_percent", "indv_foodstamps_percent"]
        },
        demographics: {
            title: "Demographics",
            variables: ["females_percent", "males_percent"]
        },
        maritalStatus: {
            title: "Marital Status",
            variables: ["widowed_percent", "never_married_percent", "married_spouse_present_percent", "divorced_percent", "separated_percent", "married_spouse_absent_percent"]
        },
        race: {
            title: "Race & Ethnicity",
            variables: ["white_percent", "black_percent", "other_race_percent", "two_minor_race_percent", "japanese_percent", "other_asian_percent", "american_indian_alaska_native_percent", "chinese_percent", "three_plus_races_percent"]
        },
        citizenship: {
            title: "Citizenship & Language",
            variables: ["citizen_percent", "non_citizen_percent", "naturalized_citizen_percent", "english_at_home_percent", "other_lang_at_home_percent"]
        },
        education: {
            title: "Education",
            variables: ["some_college_or_bachelor_percent", "high_school_or_lower_education_percent", "masters_or_professional_cert_percent", "doctoral_degree_percent"]
        },
        employment: {
            title: "Employment",
            variables: ["employed_percent", "not_in_labor_force_percent", "unemployed_percent"]
        },
        election: {
            title: "Election",
            variables: ["winner"]
        }
    };

    // Load saved variables or set defaults
    useEffect(() => {
        setVariables(loadVariableSettings());
    }, []);

    if (!loggedIn) {
        return <Navigate to="/login" />;
    }

    const handleToggle = (id, field) => {
        const newVariables = variables.map(variable => {
            if (variable.id === id) {
                return { ...variable, [field]: !variable[field] };
            }
            return variable;
        });
        
        setVariables(newVariables);
        saveVariableSettings(newVariables);
        
        setSnackbar({
            open: true,
            message: 'Settings saved successfully',
            severity: 'success'
        });
    };

    const handleAddVariable = () => {
        if (!newVariable.id || !newVariable.label || !newVariable.category) {
            setSnackbar({
                open: true,
                message: 'Please fill in all fields',
                severity: 'error'
            });
            return;
        }

        if (variables.some(v => v.id === newVariable.id) && !editMode) {
            setSnackbar({
                open: true,
                message: 'Variable ID already exists',
                severity: 'error'
            });
            return;
        }

        let updatedVariables;
        if (editMode) {
            updatedVariables = variables.map(v => 
                v.id === newVariable.id ? newVariable : v
            );
        } else {
            updatedVariables = [...variables, newVariable];
            // Update category configuration
            categories[newVariable.category].variables.push(newVariable.id);
        }

        setVariables(updatedVariables);
        saveVariableSettings(updatedVariables);
        
        setSnackbar({
            open: true,
            message: `Variable ${editMode ? 'updated' : 'added'} successfully`,
            severity: 'success'
        });

        // Reset form
        setNewVariable({ id: '', label: '', category: '', showInPopup: false, showInTrends: false });
        setEditMode(false);
        setEditingVariable(null);
    };

    const handleDelete = (variable) => {
        setVariableToDelete(variable);
        setDeleteDialogOpen(true);
    };

    const confirmDelete = () => {
        const updatedVariables = variables.filter(v => v.id !== variableToDelete.id);
        setVariables(updatedVariables);
        saveVariableSettings(updatedVariables);
        
        // Remove from category configuration
        Object.values(categories).forEach(category => {
            const index = category.variables.indexOf(variableToDelete.id);
            if (index > -1) {
                category.variables.splice(index, 1);
            }
        });

        setSnackbar({
            open: true,
            message: 'Variable deleted successfully',
            severity: 'success'
        });
        setDeleteDialogOpen(false);
        setVariableToDelete(null);
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpandedCategory(isExpanded ? panel : false);
    };

    return (
        <Box sx={{ p: 1, maxWidth: '800px', margin: '0 auto' }}>
            <Typography variant="h6" sx={{ mb: 1, fontSize: '1.1rem' }}>
                Variable Display Settings
            </Typography>
            
            {Object.entries(categories).map(([categoryId, category]) => (
                <Accordion 
                    key={categoryId}
                    expanded={expandedCategory === categoryId}
                    onChange={handleAccordionChange(categoryId)}
                    sx={{ mb: 0.5 }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ 
                            backgroundColor: 'rgba(0, 0, 0, 0.03)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.05)' }
                        }}
                    >
                        <Typography sx={{ fontSize: '0.95rem', fontWeight: 'medium' }}>
                            {category.title}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ py: 1, fontSize: '0.9rem', fontWeight: 'bold', width: '60%' }}>Variable</TableCell>
                                        <TableCell align="center" sx={{ py: 1, fontSize: '0.9rem', fontWeight: 'bold', width: '20%' }}>Popup</TableCell>
                                        <TableCell align="center" sx={{ py: 1, fontSize: '0.9rem', fontWeight: 'bold', width: '20%' }}>Trends</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {category.variables.map(varId => {
                                        const variable = variables.find(v => v.id === varId);
                                        if (!variable) return null;
                                        return (
                                            <TableRow key={variable.id} hover>
                                                <TableCell sx={{ py: 0.5, fontSize: '0.875rem' }}>{variable.label}</TableCell>
                                                <TableCell align="center" sx={{ py: 0.5 }}>
                                                    <Switch
                                                        size="small"
                                                        checked={variable.showInPopup}
                                                        onChange={() => handleToggle(variable.id, 'showInPopup')}
                                                    />
                                                </TableCell>
                                                <TableCell align="center" sx={{ py: 0.5 }}>
                                                    <Switch
                                                        size="small"
                                                        checked={variable.showInTrends}
                                                        onChange={() => handleToggle(variable.id, 'showInTrends')}
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </AccordionDetails>
                </Accordion>
            ))}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert 
                    onClose={() => setSnackbar({ ...snackbar, open: false })} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default VariableManager; 